import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import _ from 'lodash-es';
import { catchError, tap } from 'rxjs';

import { CustomerReferential, emptyCustomerReferential } from 'src/app/features/customer/models/CustomerReferential';
import { Products, emptyProducts } from '../services/models/Products';
import { ProductService } from '../services/product.service';

export class FetchAllProductsAction {
  static readonly type = '[Products] ProductsFetched';
}
export class FetchAllCustomerReferentialsAction {
  static readonly type = '[Products] CustomerReferentialsFetched';
}

export interface ProductsStateModel {
  loading: boolean;
  products: Products;
  customerReferential: CustomerReferential;
}

const defaultState = {
  loading: true,
  products: emptyProducts(),
  customerReferential: emptyCustomerReferential()
};

const getDefaultState = () => ({ ...defaultState });

@State<ProductsStateModel>({
  name: 'Products',
  defaults: getDefaultState()
})
@Injectable()
export class ProductsState {
  private productsService = inject(ProductService);
  constructor() {}

  @Action(FetchAllProductsAction)
  fetchAllProductsAction({ patchState }: StateContext<ProductsStateModel>) {
    patchState({ loading: true });
    return this.productsService.getProducts().pipe(
      tap((data) => {
        const products: Products = this.processProducts(data);
        patchState({ products, loading: false });
      }),

      catchError((error) => {
        patchState(getDefaultState());
        throw error;
      })
    );
  }

  @Action(FetchAllCustomerReferentialsAction)
  fetchAllCustomerReferentialsAction({ patchState }: StateContext<ProductsStateModel>) {
    patchState({ loading: true });
    return this.productsService.getCustomerReferential().pipe(
      tap((res) => {
        const customerReferential: CustomerReferential = res.data;
        patchState({ customerReferential, loading: false });
      }),

      catchError((error) => {
        patchState(getDefaultState());
        throw error;
      })
    );
  }

  @Selector()
  static state(state: ProductsStateModel) {
    return state;
  }

  @Selector()
  static loading(state: ProductsStateModel) {
    return state.loading;
  }

  @Selector()
  static products(state: ProductsStateModel) {
    return state.products;
  }

  @Selector()
  static customerReferential(state: ProductsStateModel) {
    return state.customerReferential;
  }

  processProducts(products: Products): Products {
    // sorting product data
    let productAttributes: keyof typeof products;
    for (productAttributes in products) {
      if (Array.isArray(products[productAttributes])) {
        products[productAttributes] = _.sortBy(products[productAttributes]);
      }
    }
    return products;
  }
}
